/**
 * Stratifi component module.
 * @module advisor/components/partners/Stratifi
 *
 */
import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {Icon} from '../../../shared';
import {fill} from '../../../shared/components/cssUtils';

const _Score = styled.div`
  height: ${props => {
    if (props.isEmpty) {
      return (props.size === 'sm') ? '24px' : '56px';
    }

    return 'fit-content';
  }};

  width: ${props => {
    if (props.isEmpty) {
      return (props.size === 'sm') ? '24px' : '56px';
    }

    return 'fit-content';
  }};

  font-family: RobotoBold;
  font-size: ${props => (props.size === 'sm') ? '14px' : '34px'};
  color: #fff;
`;

const _Background = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  height: ${props => {
    if (props.size === 'sm') {return '24px';}
    if (props.size) {return '56px';}

    return fill(null);
  }};

  width: ${props => {
    if (props.size === 'sm') {return props.value ? '30px' : '24px';}
    if (props.size) {return props.value ? '70px' : '56px';}

    return fill(null);
  }};

  border-radius: ${props => (props.size === 'sm') ? '4px' : '8px'};

  background-color: ${props => {
    if (!props.value) {return 'none';}
    if (props.value <= 5.4) {return '#4eb63d';}
    if (props.value <= 7.4) {return '#f7941d';}
    if (props.value > 7.4) {return '#c93539';}
  }};
`;

const _Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  height: ${props => {
    if (props.size === 'sm') {return '24px';}
    if (props.size) {return '56px';}

    return fill(null);
  }};

  width: ${props => {
    if (props.size === 'sm') {return props.value ? '30px' : '24px';}
    if (props.size) {return props.value ? '70px' : '56px';}

    return fill(null);
  }};

  border-radius: ${props => (props.size === 'sm') ? '4px' : '8px'};
  background-color: ${props => (props.isPolling) ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)'};
`;

const _Clock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;

  height: ${props => {
    if (props.size === 'sm') {return '24px';}
    if (props.size) {return '56px';}

    return fill(null);
  }};

  width: ${props => {
    if (props.size === 'sm') {return props.value ? '30px' : '24px';}
    if (props.size) {return props.value ? '70px' : '56px';}

    return fill(null);
  }};

  border-radius: ${props => (props.size === 'sm') ? '4px' : '8px'};
`;

/**
 * Stratifi component
 * @name Stratifi
 */
const Stratifi = props => {
  const {dataPoint, size, isPolling} = props;

  let riskValue = !isPolling ? <Icon svg={'noScore'}
                                     height={(size === 'sm') ? '24px' : '56px'}
                                     width={(size === 'sm') ? '24px' : '56px'}/> : null;

  if (dataPoint.value === 10) {
    riskValue = '10';
  } else if (dataPoint.value) {
    riskValue = dataPoint.value.toFixed(1);
  }

  return <_Background size={size} value={dataPoint.value}>
    <_Overlay size={size} value={dataPoint.value} isPolling={isPolling}/>
    {isPolling
      ? <_Clock size={size} value={dataPoint.value}>
        <Icon
          svg={'/icons/stratifi_wait_icon.svg'}
          height={(size === 'sm') ? '12px' : '20px'}
          width={(size === 'sm') ? '12px' : '20px'}
        />
      </_Clock>
      : null}
    <_Score size={size} isEmpty={!riskValue}>
      {riskValue}
    </_Score>
  </_Background>;
};

export default Stratifi;

Stratifi.propTypes = {
  /**
   * the data to be bound to the parent (the PRISM rating)
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.number,
  }).isRequired,
  /**
   * The size of the component ('lg' or 'sm')
   */
  size: PropTypes.string.isRequired,
  /**
   * Whether the Stratifi is currently being polled
   */
  isPolling: PropTypes.bool,
};
